import { Component, forwardRef, Input, OnInit, input, output, viewChild } from "@angular/core";
import { ControlValueAccessor, NgForm, NgModel, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { Observable, takeUntil } from "rxjs";
import { SharedControl } from "../../classes/sharedcontrol";
import { MasterTask } from "../../types";
import { AccountCategory } from '../../types/accountcategory';

import { ErrormessageComponent } from "../validation/errormessage/errormessage.component";
import { AutoCompleteModule } from "primeng/autocomplete";
import { FluidModule } from "primeng/fluid";

@Component({
    selector: "pcp-accountcategory-autocomplete",
    templateUrl: "./pcp-accountcategory-autocomplete.component.html",
    styleUrls: [],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PCPAccountCategoryAutoCompleteComponent),
            multi: true
        }
    ],
    standalone: true,
    imports: [AutoCompleteModule, FormsModule, FluidModule, ErrormessageComponent]
})
export class PCPAccountCategoryAutoCompleteComponent extends SharedControl {
    readonly accountCategorySelection = viewChild<NgModel>("accountCategorySelection");

    set accountCategories(accountCategories: AccountCategory[]) {
        this._accountCategories = accountCategories;
        this.accountCategorySuggestions = this.accountCategoriesToAutoCompleteSuggestions(accountCategories);
    }


    _accountCategories: AccountCategory[] = [];
    allAccountCategories: AccountCategory[] = [];
    accountCategorySuggestions: AccountCategory[] = [];

    _observable: Observable<AccountCategory[]>;
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set accountCategoriesObservable(observable: Observable<AccountCategory[]>) {
        this._observable = observable;
        this.reloadOptions();
    };

    get accountCategoriesObservable(): Observable<AccountCategory[]> {
        return this._observable;
    }

    readonly appendTo = input<string>(undefined);
    // TODO: Skipped for migration because:
    //  This input is inherited from a superclass, but the parent cannot be migrated.
    @Input() disabled: boolean = false;
    readonly forceSelection = input<boolean>(true);
    readonly isOneOfManyRequired = input<boolean>(false);
    readonly maxSelectedLabels = input<number>(1);
    readonly xOfManyErrors = input<boolean>(false);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    @Input() placeholder: string = "";
    readonly required = input<boolean>(false);
    readonly styleClass = input<string>("");

    _selectedAccountCategory: AccountCategory;

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set selectedAccountCategory(acctCategory) {
        this._selectedAccountCategory = acctCategory;
        this.propagateChange(this._selectedAccountCategory);
    };

    get selectedAccountCategory(): AccountCategory {
        return this._selectedAccountCategory;
    };

    readonly selectedAccountCategoryChange = output<AccountCategory>();

    readonly onCompleteFilter = input<(filterString: string, accountCategoriesToFilter: AccountCategory[]) => void>(this.filter);
    readonly onFocusEvent = output<any>();

    writeValue(obj: any): void {
        this.selectedAccountCategory = obj;
    }

    accountCategoriesToAutoCompleteSuggestions(accountCategories: AccountCategory[]) {
        const byDisplayText = (a, b) => {
            //Force 'Internal Task' to the top
            if (a.name.trim() == "Internal Task" || b.name.trim() == "Internal Task")
                return 1;
            else
                return a.displayText > b.displayText ? 1 : -1
        };

        accountCategories = accountCategories.map((accountCategory: AccountCategory) => {
            accountCategory.displayText = accountCategory.number !== "" ? `${accountCategory.number} - ${accountCategory.name.trim()}` : accountCategory.name.trim();
            return accountCategory;
        }).sort(byDisplayText);

        return accountCategories;
    }

    clearAccountCategory(): void {
        this.selectedAccountCategory = null;
        this.selectedAccountCategoryChange.emit(this.selectedAccountCategory);
    }

    reloadOptions(): void {
        if (!this.accountCategoriesObservable) { return; }

        const originalPlaceholder = this.placeholder;
        this.placeholder = "Loading...";
        this.accountCategoriesObservable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((accountCategoryOptions) => {
                this.accountCategories = accountCategoryOptions;
                this.allAccountCategories = accountCategoryOptions;
                this.placeholder = originalPlaceholder;
            });
    }

    filter(event: any, accountCategories: AccountCategory[]) {
        const query = event || "";
        const theFilterWasCleared: boolean = query === "";

        if (theFilterWasCleared) {
            this.accountCategorySuggestions = this.accountCategoriesToAutoCompleteSuggestions(accountCategories);
            return;
        }

        const filteredAccountCategories = accountCategories.filter((accountCategoryOption: AccountCategory) => {
            return accountCategoryOption.displayText.toLowerCase().indexOf(query.toLowerCase()) !== -1;
        });

        this.accountCategorySuggestions = filteredAccountCategories;
    }

    onComplete(event: any) {
        const filterString: string = event && event.query || "";
        const fn = this.onCompleteFilter();
        if (fn) {
            fn.call(this, filterString, this._accountCategories);
        }
    }

    onFocus(): void {
        // TODO: The 'emit' function requires a mandatory any argument
    this.onFocusEvent.emit(null);
    }

    onBlur(): void {
        this.propagateModelTouched();
    }

    onSelect(): void {
        this.selectedAccountCategoryChange.emit(this.selectedAccountCategory);
    }

    filterByMasterTasks(masterTasks: MasterTask[]) {
        this.accountCategorySuggestions = this.allAccountCategories.filter(ac => masterTasks.some(mt => mt.accountCategory.accountCategoryId === ac.accountCategoryId));
    }
}
